import { DatePipe, formatDate, NgForOf, NgIf, NgStyle } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, EMPTY, filter, firstValueFrom, Observable, of, Subscription, tap, timer } from 'rxjs';
import { CalendarDTO } from 'src/app/interfaces/calendar';
import { EventViewDTO } from 'src/app/interfaces/event';
import { createTask, TaskDTO } from 'src/app/interfaces/task';
import { UserDTO } from 'src/app/interfaces/user';
import { EventService } from 'src/app/services/event.service';
import { TaskService } from 'src/app/services/task.service';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { TaskComponent } from '../task/task.component';
import { CalendarService } from 'src/app/services/calendar.service';
import { NoteComponent } from '../note/note.component';
import { NoteService } from 'src/app/services/note.service';
import { SettingsDTO } from 'src/app/interfaces/settings';
import { SettingsService } from 'src/app/services/settings.service';
import { createNote, NoteDTO } from 'src/app/interfaces/note';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressBar } from '@angular/material/progress-bar';
import { WelcomeDialogComponent } from '../welcome-dialog/welcome-dialog.component';
import { CreateConsentComponent } from '../create-consent/create-consent.component';
import { VisibilityService } from 'src/app/services/visibility.service';
import { EventComponent } from '../event/event.component';
import { RRule, RRuleSet } from 'rrule';
import { CalendarIntegrationDTO } from 'src/app/interfaces/calendarIntegration';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { FunctionsService } from 'src/app/services/functions.service';

interface SelectionSlot {
  date: Date;
  topPx: number;
  heightPx: number;
}

export interface ExpandedEvent extends EventViewDTO {
  instanceStart: Date;
  instanceEnd: Date;
}

@Component({
  selector: 'app-dashboard',
  imports: [MatIconModule, MatCheckboxModule, MatButtonModule, DatePipe, MatTooltipModule, NgForOf, NgIf, MatButtonToggleModule, MatProgressBar, MatFormFieldModule,
    MatSelectModule, MatIconModule, MatOptionModule, MatMenuModule, MatButtonModule, MatDividerModule, NgStyle
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, AfterViewInit {

  user: UserDTO;
  isLoggedIn = false;
  selectedSlot: SelectionSlot | null = null;
  currentCalendar: CalendarDTO;
  calendars: CalendarDTO[] = [];
  meetingTasks: TaskDTO[] = [];
  upcomingTasks: TaskDTO[] = [];
  weekPlan: any[] = [];
  settings: SettingsDTO;
  startDate: Date = new Date();
  outlookDays: number = 14;
  isTasksLoading: boolean = false;
  isCalendarLoading: boolean = false;
  hourSlotHeight = 56;
  hours = Array.from({ length: 24 }, (_, i) => i);
  @ViewChild('timeGridScroll')
  timeGridScroll?: ElementRef<HTMLDivElement>;
  timedEventLayout = new Map<string, { left: number, width: number }>();
  emptyCalendarIntegrations: CalendarIntegrationDTO[] = [];
  popupIsShowing: boolean = false;
  private calendarRefreshSub?: Subscription;
  private readonly CALENDAR_REFRESH_INTERVAL = 5 * 60 * 1000;
  private lastRefreshTime: number = 0;
  private route = inject(ActivatedRoute);
  editEventEnabled: boolean = false;
  selectedCalendarForTaskOverview: CalendarDTO;
  rawEvents: EventViewDTO[] = [];

  constructor(public matDialog: MatDialog, private tokenStorageService: TokenStorageService, private router: Router, private eventService: EventService,
    private taskService: TaskService, private calendarService: CalendarService, private noteService: NoteService, private settingsService: SettingsService,
    private cdr: ChangeDetectorRef, private visibilityService: VisibilityService, @Inject(LOCALE_ID) private locale: string,
    public functions: FunctionsService) {
    this.setToCurrentWeek();
  }

  async ngOnInit() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (!this.isLoggedIn) {
      this.router.navigate(['home']);
      return;
    }
    this.user = this.tokenStorageService.getUser();
    this.editEventEnabled = await (this.functions.hasFeature("EDITEVENT"));

    try {
      this.calendars = await firstValueFrom(this.calendarService.getCalendarsForDashboard());
      this.calendars.unshift({
        id: -1,
        userId: this.user.id,
        calendarIntegrationId: -1,
        calendarIntegrationName: '',
        externalCalendarId: '',
        summary: '<All>',
        showInDashboard: false,
        showInNotes: false,
        uuid: '',
        taskUuid: '',
        color: '',
        url: ''
      })
    } catch (error) {

    }
    if (this.user.calendarId > 0) {
      this.currentCalendar = await firstValueFrom(this.calendarService.getCalendar(this.user.calendarId));
      this.selectedCalendarForTaskOverview = this.currentCalendar;
    }
    try {
      this.settings = await firstValueFrom(this.settingsService.getSettings());
    } catch (error) {

    }

    this.getCalendarData().subscribe();
    this.getTaskData().subscribe();

    const showWelcomePopup = this.route.snapshot.queryParams['showWelcomePopup'];
    if (showWelcomePopup && this.settings.showWelcomePopup) {
      if (!this.popupIsShowing) {
        this.showTheWelcomePopup();
      }
    }
    this.calendarRefresh()
  }

  calendarRefresh() {
    this.calendarRefreshSub = combineLatest([
      this.visibilityService.isActive$,
      timer(0, 30000)
    ]).pipe(
      // Alleen doorgaan als de app in focus is
      filter(([isActive, _]) => isActive),
      // Alleen doorgaan als het langer dan 5 min geleden is (of de 1e keer)
      filter(() => {
        const rightNow = Date.now();
        return (rightNow - this.lastRefreshTime) >= this.CALENDAR_REFRESH_INTERVAL;
      })
    ).subscribe(() => {
      this.refreshCalendarData();
    });

  }

  refreshCalendarData() {
    this.lastRefreshTime = Date.now();
    setTimeout(() => this.getCalendarData().subscribe(), 0);
  }

  onCalendarSelectChange(calendar: CalendarDTO | null): void {
    this.selectedCalendarForTaskOverview = calendar;
    this.getTaskData().subscribe();
  }

  onOutlookDaysChange(days: number): void {
    this.outlookDays = days || 0;
    this.getTaskData().subscribe();
  }

  ngOnDestroy() {
    this.calendarRefreshSub?.unsubscribe();
  }

  showTheWelcomePopup() {
    this.popupIsShowing = true;
    const dialogRef = this.matDialog.open(WelcomeDialogComponent, {
      width: '530px',
      height: '530px',
      panelClass: 'squaredDialog',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.createConsent();
      }
      this.popupIsShowing = false;
    });
  }

  getWeekRangeString(startDate: Date, endDate: Date): string {
    if (!startDate || !endDate) return '';

    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    // Bepaal de formats op basis van de locale
    // Voor NL: 'd MMM', voor US: 'MMM d'
    const dayMonthFormat = this.getDayMonthFormat();
    const fullFormat = `${dayMonthFormat} yyyy`;

    if (startYear !== endYear) {
      // Verschillende jaren: dd MMM yyyy - dd MMM yyyy
      return `${formatDate(startDate, fullFormat, this.locale)} - ${formatDate(endDate, fullFormat, this.locale)}`;
    } else {
      // Hetzelfde jaar: dd MMM - dd MMM yyyy
      return `${formatDate(startDate, dayMonthFormat, this.locale)} - ${formatDate(endDate, fullFormat, this.locale)}`;
    }
  }

  // Hulpmethode om de volgorde van dag/maand te bepalen o.b.v. de locale
  private getDayMonthFormat(): string {
    // Voorbeeld check: in de meeste locales betekent een format waarbij de maand voorop staat (US) 
    // dat we 'MMM d' willen, anders 'd MMM' (NL/EU).
    const sample = formatDate(new Date(2000, 11, 25), 'shortDate', this.locale);
    if (sample.indexOf('12') < sample.indexOf('25')) {
      return 'MMM dd';
    }
    return 'dd MMM';
  }

  createConsent() {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.data = this.emptyCalendarIntegrations;
    dialogConfig.height = "480px"; //678
    dialogConfig.width = "1008px"; //1440
    const modalDialog = this.matDialog.open(CreateConsentComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
    });
  }

  setToCurrentWeek() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Zondag, 1 = Maandag, ..., 6 = Zaterdag

    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);

    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0); // Zet tijd op midnight voor schone vergelijkingen
    this.startDate = monday;
  }

  onViewChange(event: any) {
    if (event.value === 'week') {
      this.setToCurrentWeek();
      this.getCalendarData().subscribe();
    } else {
      // Voor 'Next 7 days' begin je gewoon bij vandaag
      this.startDate = new Date();
      this.startDate.setHours(0, 0, 0, 0);
      this.getCalendarData().subscribe();
    }
  }

  today() {
    this.setToCurrentWeek();
    this.getCalendarData().subscribe();
  }

  previousWeek() {
    const newDate = new Date(this.startDate);
    newDate.setDate(newDate.getDate() - 7);
    this.startDate = newDate;
    this.getCalendarData().subscribe();

    this.cdr.detectChanges();
    setTimeout(() => this.scrollAgendaToHour(9), 0);
  }

  nextWeek() {
    const newDate = new Date(this.startDate);
    newDate.setDate(newDate.getDate() + 7);
    this.startDate = newDate;
    this.getCalendarData().subscribe();

    this.cdr.detectChanges();
    setTimeout(() => this.scrollAgendaToHour(9), 0);
  }

  getCalendarLabel = (calendar: CalendarDTO) => {
    const duplicates = this.calendars.filter(c => c.summary === calendar.summary);
    if (duplicates.length > 1 && calendar.calendarIntegrationName) {
      return `${calendar.summary} (${calendar.calendarIntegrationName})`;
    }
    return calendar.summary;
  };

  getCalendarData(): Observable<any> {
    this.isCalendarLoading = true;
    const start = this.startDate;
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 7);

    return this.eventService.getDashboardOverview(start, end).pipe(
      tap({
        next: (data: any) => {
          this.rawEvents = data || [];
          this.buildWeekPlan(this.rawEvents, start);
          this.calculateMultiDayLanes();
          this.isCalendarLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => this.scrollAgendaToHour(9), 0);
        }
      }),
      catchError(error => {
        this.isCalendarLoading = false;
        console.error("One of the API calls failed:", error);
        return EMPTY;
      })
    );
  }

  upsertEventLocally(storedEvent: EventViewDTO, scope: string = 'ALL_EVENTS', instanceStartDate?: string): void {
    if (!storedEvent) return;

    if (scope === 'THIS_INSTANCE' && instanceStartDate) {
      // A. SINGLE INSTANCE UPDATE:
      // Voeg de startdatum van de aangepaste herhaling toe aan de EXDATE van het hoofdevent
      const master = this.rawEvents.find(e =>
        (storedEvent.recurringEventId && e.eventId === storedEvent.recurringEventId) ||
        e.id === storedEvent.id
      );
      if (master) {
        const ex = master.exdate ? master.exdate.split(',') : [];
        if (!ex.includes(instanceStartDate)) {
          ex.push(instanceStartDate);
          master.exdate = ex.join(',');
        }
      }
      // Voeg het nieuwe uitzonderings-event toe
      const existingIdx = this.rawEvents.findIndex(e => e.id === storedEvent.id || (storedEvent.eventId && e.eventId === storedEvent.eventId));
      if (existingIdx >= 0) {
        this.rawEvents[existingIdx] = storedEvent;
      } else {
        this.rawEvents.push(storedEvent);
      }
    } else {
      // B. REEKS OF GEWOON EVENT UPDATE:
      const index = this.rawEvents.findIndex(e =>
        (storedEvent.id && storedEvent.id > 0 && e.id === storedEvent.id) ||
        (storedEvent.eventId && e.eventId === storedEvent.eventId)
      );

      if (index >= 0) {
        this.rawEvents[index] = storedEvent; // Overschrijf bestaand event
      } else {
        this.rawEvents.push(storedEvent); // Voeg nieuw event toe
      }
    }

    // Herbereken direct het weekplan in de UI
    this.buildWeekPlan(this.rawEvents, this.startDate);
  }

  removeEventLocally(eventId: number, scope: string, occurrenceDate?: string): void {
    if (scope === 'THIS_INSTANCE' && occurrenceDate) {
      // Enkele herhaling verwijderen: voeg de datum toe aan EXDATE van de master
      const master = this.rawEvents.find(e => e.id === eventId);
      if (master) {
        const ex = master.exdate ? master.exdate.split(',') : [];
        if (!ex.includes(occurrenceDate)) {
          ex.push(occurrenceDate);
          master.exdate = ex.join(',');
        }
      }
    } else {
      // Hele reeks of los event verwijderen
      this.rawEvents = this.rawEvents.filter(e => e.id !== eventId);
    }

    // Herbereken direct het weekplan
    this.buildWeekPlan(this.rawEvents, this.startDate);
  }

  getTaskData() {
    this.isTasksLoading = true;

    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + this.outlookDays);

    let calendarId = -1;
    if (this.selectedCalendarForTaskOverview) calendarId = this.selectedCalendarForTaskOverview.id;
    return this.taskService.getTasksForDueDate(calendarId, end).pipe(

      tap({
        next: (data: any) => {
          this.upcomingTasks = data;
          this.isTasksLoading = false;
        }
      }),

      catchError(error => {
        this.isTasksLoading = false;
        console.error("One of the API calls failed:", error);
        return EMPTY; // stop the stream
      })

    );

  }

  getTaskCardStyle(task: TaskDTO) {
    if (!task) return {};

    if (task.calendarColor) {
      return {
        'background-color': `${task.calendarColor}0d`, // 26 is hex voor ~15% transparantie
      };
    } else return {};
  }

  expandEvents(events: EventViewDTO[], windowStart: Date, windowEnd: Date): ExpandedEvent[] {
    const expanded: ExpandedEvent[] = [];

    for (const event of events) {
      if (!event.startDate || !event.endDate) continue;

      let exceptionDates = new Set<number>();

      // 2. Pas unshifting ALLEEN toe op All-Day events. 
      //    Normale afspraken krijgen gewoon de echte lokale tijdzone (+2u).
      const start = event.allDay
        ? this.parseUtcToUnshiftedDate(event.startDate)
        : new Date(event.startDate);

      const end = event.allDay
        ? this.parseUtcToUnshiftedDate(event.endDate)
        : new Date(event.endDate);

      const durationMs = end.getTime() - start.getTime();

      // 3. NIET-HERHALENDE EVENTS
      if (!event.isRecurring || !event.rrule) {
        expanded.push({
          ...event,
          instanceStart: start,
          instanceEnd: end
        });
        continue;
      }

      // 4. HERHALENDE EVENTS (RRULE)
      try {
        const utcDtStart = event.allDay
          ? new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0))
          : new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), start.getHours(), start.getMinutes(), 0));

        const ruleOptions = RRule.parseString(event.rrule);
        ruleOptions.dtstart = utcDtStart;

        if (ruleOptions.freq === RRule.YEARLY && ruleOptions.bymonthday && !ruleOptions.bymonth) {
          ruleOptions.bymonth = [start.getMonth() + 1];
        }

        const rruleSet = new RRuleSet();
        rruleSet.rrule(new RRule(ruleOptions));


        if (event.exdate) {
          exceptionDates = this.functions.parseICalExdatesToEpoch(event.exdate);
          for (const epoch of exceptionDates) {
            rruleSet.exdate(new Date(epoch));
          }
        }

        const searchStart = new Date(Date.UTC(
          windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate() - 1
        ));
        const searchEnd = new Date(Date.UTC(
          windowEnd.getFullYear(), windowEnd.getMonth(), windowEnd.getDate() + 1
        ));

        const occurrences = rruleSet.between(searchStart, searchEnd, true);

        occurrences.forEach(occ => {
          const occStart = event.allDay
            ? new Date(occ.getUTCFullYear(), occ.getUTCMonth(), occ.getUTCDate(), 0, 0, 0)
            : new Date(occ.getUTCFullYear(), occ.getUTCMonth(), occ.getUTCDate(), occ.getUTCHours(), occ.getUTCMinutes(), 0);

          if (exceptionDates.has(occStart.getTime())) {
            return;
          }

          const occEnd = new Date(occStart.getTime() + durationMs);

          if (occEnd >= windowStart && occStart <= windowEnd) {
            expanded.push({
              ...event,
              id: event.id,
              startDate: occStart.toISOString() as any,
              endDate: occEnd.toISOString() as any,
              instanceStart: occStart,
              instanceEnd: occEnd
            });
          }
        });

      } catch (err) {
        console.error(`Fout bij verwerken RRULE voor event ${event.id}:`, err);
        expanded.push({
          ...event,
          instanceStart: start,
          instanceEnd: end
        });
      }
    }

    return expanded;
  }

  private parseUtcToUnshiftedDate(dateInput: any): Date {
    if (!dateInput) return new Date();
    const d = new Date(dateInput);
    // Gebruik de UTC-jaar/maand/dag/uur/minuut van de ontvangen ISO string
    // en maak daar een lokale Date van zodat de tijdzone-offset effectief 0 wordt.
    return new Date(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds()
    );
  }

  buildWeekPlan(rawEvents: EventViewDTO[], preferredStartDate: Date) {
    const startDate = new Date(preferredStartDate);
    startDate.setHours(0, 0, 0, 0);

    const windowEnd = new Date(startDate);
    windowEnd.setDate(startDate.getDate() + 7);

    const events = this.expandEvents(rawEvents, startDate, windowEnd);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Gebruik de LOKALE YYYY-MM-DD formatting (geen UTC!)
    const toLocalYYYYMMDD = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const todayStr = toLocalYYYYMMDD(today);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);

      const currentDayStr = toLocalYYYYMMDD(currentDate);

      const dayEvents = (events || [])
        .filter(event => {
          // Gebruik de reeds unshifted instanceStart/instanceEnd of ge-parste startDate/endDate
          const eventStart = new Date(event.instanceStart || event.startDate);
          const eventEnd = new Date(event.instanceEnd || event.endDate);

          const eventStartStr = toLocalYYYYMMDD(eventStart);

          // CHECK LOKALE UREN: eventEnd is door parseUtcToUnshiftedDate lokaal op 00:00 gezet
          const isMidnightEnd = eventEnd.getHours() === 0 && eventEnd.getMinutes() === 0;

          const adjustedEnd = (isMidnightEnd && eventEnd.getTime() > eventStart.getTime())
            ? new Date(eventEnd.getTime() - 1)
            : eventEnd;

          const eventEndStr = toLocalYYYYMMDD(adjustedEnd);

          return currentDayStr >= eventStartStr && currentDayStr <= eventEndStr;
        })
        .map(event => ({
          ...event,
          startTime: this.formatTime(new Date(event.instanceStart || event.startDate)),
          calendarColor: event.calendarColor || '#3b82f6'
        }))
        .sort((a, b) => a.startTime.localeCompare(b.startTime));

      days.push({
        date: currentDate,
        isToday: currentDayStr === todayStr,
        events: dayEvents
      });
    }

    this.weekPlan = days;
    this.calculateMultiDayLanes();
    this.calculateTimedLayout();
  }

  calculateTimedLayout(): void {
    this.timedEventLayout.clear();
    if (!this.weekPlan) return;

    this.weekPlan.forEach(day => {
      const events = this.getTimedEventsForDay(day);
      if (!events.length) return;

      // 1. Maak lichte werk-objecten met voorgerekende timestamps
      const items = events.map(event => ({
        key: this.getEventKey(event),
        start: this.toDate(event.startDate).getTime(),
        end: this.toDate(event.endDate).getTime(),
        colIndex: 0,
        colSpan: 1,
        event
      }));

      // 2. Groepeer in overlappende clusters (afspraken die elkaar in de tijd raken)
      const clusters: typeof items[] = [];
      let currentCluster: typeof items = [];
      let clusterEnd = -1;

      for (const item of items) {
        if (currentCluster.length === 0) {
          currentCluster.push(item);
          clusterEnd = item.end;
        } else if (item.start < clusterEnd) {
          currentCluster.push(item);
          clusterEnd = Math.max(clusterEnd, item.end);
        } else {
          clusters.push(currentCluster);
          currentCluster = [item];
          clusterEnd = item.end;
        }
      }
      if (currentCluster.length > 0) {
        clusters.push(currentCluster);
      }

      // 3. Verwerk elk cluster (plaats in kolommen + bereken optionele verbreding)
      clusters.forEach(cluster => {
        const columns: typeof items[] = [];

        // 3a. Wijs de eerste beschikbare kolom toe
        cluster.forEach(item => {
          let placed = false;

          for (let colIdx = 0; colIdx < columns.length; colIdx++) {
            const lastItemInCol = columns[colIdx][columns[colIdx].length - 1];

            // Check of het event past achter de laatste in deze kolom
            if (item.start >= lastItemInCol.end) {
              columns[colIdx].push(item);
              item.colIndex = colIdx;
              placed = true;
              break;
            }
          }

          if (!placed) {
            columns.push([item]);
            item.colIndex = columns.length - 1;
          }
        });

        const totalColumns = columns.length;
        const colWidth = 100 / totalColumns;

        // 3b. Bereken colSpan (uitrekken naar lege ruimte aan de rechterkant)
        cluster.forEach(item => {
          let span = 1;

          for (let nextCol = item.colIndex + 1; nextCol < totalColumns; nextCol++) {
            // Check of er in de kolom rechts een event staat dat in de tijd overlapt
            const hasOverlap = columns[nextCol].some(
              other => item.start < other.end && item.end > other.start
            );

            if (hasOverlap) {
              break; // Meteen stoppen bij de eerste blokkade
            }

            span++;
          }

          item.colSpan = span;
        });

        // 4. Ken de definitieve posities en breedtes toe aan de layout Map
        cluster.forEach(item => {
          const left = item.colIndex * colWidth;
          const width = item.colSpan * colWidth;

          this.timedEventLayout.set(item.key, { left, width });
        });
      });
    });
  }

  editTask(task: TaskDTO) {
    if (task != null) {
      const dialogConfig = new MatDialogConfig();
      // The user can't close the dialog by clicking outside its body
      dialogConfig.disableClose = false;
      dialogConfig.id = "modal-component";
      dialogConfig.panelClass = "squaredDialog";
      dialogConfig.data = task;
      dialogConfig.height = "530px";
      dialogConfig.width = "750px";
      const modalDialog = this.matDialog.open(TaskComponent, dialogConfig);
      modalDialog.afterClosed().subscribe(data => {
        if (data) {
          var task = data.task;
          if (task != null) {
            this.saveTask(task, true);
          }
        }
      });
    }
  }

  async saveTask(task: TaskDTO, reload: boolean) {
    const data = await firstValueFrom(this.taskService.saveTask(task.id, createTask(task.calendarId, task.title, task.text, task.status, task.creationDate, task.dueDate, task.statusDate, task.priority, task.sequence, task.noteId)));
    this.getTaskData().subscribe();
  }

  isTaskOverdue(task: TaskDTO): boolean {
    return task.dueDate < new Date();
  }

  async openNote(event: EventViewDTO) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.height = "580px";
    dialogConfig.width = "900px";
    dialogConfig.data = await firstValueFrom(this.noteService.createNoteForEvent(event.id, new Date(event.startDate).toISOString()));
    const modalDialog = this.matDialog.open(NoteComponent, dialogConfig);
    modalDialog.afterClosed().subscribe();
  }

  async saveNote(note: NoteDTO) {
    const data = await firstValueFrom(this.noteService.saveNote(note.id, createNote(note.calendarId, note.eventId, note.title, note.stringText, note.noteType, note.archived, note.recurring, note.participants, note.hiddenTag, note.deleted, note.parentNoteId, note.endDate, note.uuid, note.lastModifiedTime, note.version)));
  }

  getTimeZone(): string {
    if (this.settings?.timeZone) {
      return this.settings.timeZone;
    }
    return 'Europe/Amsterdam';
  }

  ngAfterViewInit(): void {
    this.scrollAgendaToHour(9);

    setTimeout(() => this.scrollAgendaToHour(9), 0);
    setTimeout(() => this.scrollAgendaToHour(9), 150);
  }

  scrollAgendaToHour(hour: number): void {
    if (!this.timeGridScroll?.nativeElement) {
      return;
    }
    this.timeGridScroll.nativeElement.scrollTop = hour * this.hourSlotHeight;
  }

  formatHour(hour: number): string {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  formatEventTime(event: any): string {
    const start = this.toDate(event.startDate);
    const end = this.toDate(event.endDate);
    return `${this.formatTime(start)} - ${this.formatTime(end)}`;
  }

  formatTime(date: Date): string {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  /**
   * Controleert of een event een hele dag (of langer) beslaat.
   * In deze gevallen willen we vaak geen specifieke tijden (00:00 - 23:59) tonen.
   */
  isFullDayOrLonger(event: any): boolean {
    // Dit kan nu simpelweg refereren naar de vernieuwde check hierboven
    return this.isMultiDayEvent(event);
  }

  buildEventTooltip(event: any): string {
    if (this.isFullDayOrLonger(event)) {
      return event.title;
    }
    if (event.location != null && event.location !== '' && event.location.toLowerCase() !== 'not shared') {
      return `${this.formatEventTime(event)} • ${event.title}\n\n${event.location}`;
    } else {
      return `${this.formatEventTime(event)} • ${event.title}`;
    }
  }

  getTimedEventsForDay(day: any): any[] {
    const events = (day.events || [])
      .filter((event: any) => !!event.startDate && !!event.endDate)
      .filter((event: any) => !this.isMultiDayEvent(event));

    // Dedupliceren op basis van unieke key (eventId/recurringEventId + starttijd)
    const uniqueEventsMap = new Map<string, any>();
    for (const ev of events) {
      const key = `${ev.recurringEventId || ev.eventId || ev.id}_${new Date(ev.startDate).getTime()}`;
      if (!uniqueEventsMap.has(key) || ev.recurringEventId !== null) {
        uniqueEventsMap.set(key, ev);
      }
    }

    return Array.from(uniqueEventsMap.values())
      .sort((a: any, b: any) => this.toDate(a.startDate).getTime() - this.toDate(b.startDate).getTime());
  }

  getUniqueWeekEvents(): any[] {
    const allEvents = (this.weekPlan || []).flatMap((day: any) => day.events || []);

    return allEvents.filter((event: any, index: number, arr: any[]) => {
      const key = this.getEventKey(event);
      return index === arr.findIndex((candidate: any) => this.getEventKey(candidate) === key);
    });
  }

  getMultiDayEvents(): any[] {
    return this.getUniqueWeekEvents()
      .filter((event: any) => this.isMultiDayEvent(event))
      .sort((a: any, b: any) => this.toDate(a.startDate).getTime() - this.toDate(b.startDate).getTime());
  }

  getMultiDayEventsStartingOn(dayIndex: number): any[] {
    return this.getMultiDayEvents().filter((event: any) => this.getVisibleStartDayIndex(event) === dayIndex);
  }

  hasMultiDayEventOnDay(day: any): boolean {
    return this.getMultiDayEvents().some((event: any) => this.eventOccursOnDay(event, day.date));
  }

  isMultiDayEvent(event: any): boolean {
    if (!event.startDate || !event.endDate) return false;

    // Als het een expliciet All-Day event is, hoort hij altijd in de bovenste balk
    //if (this.isAllDay(event)) return true;
    if (event.allDay) return true;

    const start = new Date(event.startDate);
    const end = new Date(event.endDate);

    // Check of het event over meerdere kalenderdagen verspreid is (lokale tijd)
    const isDifferentDay = start.getFullYear() !== end.getFullYear() ||
      start.getMonth() !== end.getMonth() ||
      start.getDate() !== end.getDate();

    const diffInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

    return isDifferentDay || diffInMinutes >= 1439;
  }

  getEventTop(event: any): number {
    const start = this.toDate(event.startDate);
    const minutes = (start.getHours() * 60) + start.getMinutes();
    return (minutes / 60) * this.hourSlotHeight;
  }

  getEventHeight(event: any): number {
    const start = this.toDate(event.startDate);
    const end = this.toDate(event.endDate);
    const diffMinutes = Math.max(30, (end.getTime() - start.getTime()) / 60000);
    return (diffMinutes / 60) * this.hourSlotHeight;
  }

  getEventLeft(event: any, day: any): number {
    return this.timedEventLayout.get(this.getEventKey(event))?.left ?? 0;
  }

  getEventWidth(event: any, day: any): number {
    return this.timedEventLayout.get(this.getEventKey(event))?.width ?? 100;
  }

  eventsOverlap(a: any, b: any): boolean {
    const startA = this.toDate(a.startDate).getTime();
    const endA = this.toDate(a.endDate).getTime();
    const startB = this.toDate(b.startDate).getTime();
    const endB = this.toDate(b.endDate).getTime();

    return startA < endB && startB < endA;
  }

  private toLocalString(dateInput: any): string {
    const d = new Date(dateInput);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getVisibleStartDayIndex(event: any): number {
    if (!this.weekPlan?.length) return 0;

    const eventStart = new Date(event.instanceStart || event.startDate);
    const eventStartStr = this.toLocalString(eventStart);
    const weekDaysStr = this.weekPlan.map(d => this.toLocalString(d.date));

    if (eventStartStr < weekDaysStr[0]) {
      return 0;
    }

    const index = weekDaysStr.indexOf(eventStartStr);
    return index >= 0 ? index : 0;
  }

  getVisibleEndDayIndex(event: any): number {
    if (!this.weekPlan?.length) return 0;

    const eventStart = new Date(event.instanceStart || event.startDate);
    const eventEnd = new Date(event.instanceEnd || event.endDate);

    // Als het event lokaal om 00:00:00 eindigt, valt de eindtijd feitelijk op 23:59:59 van de vorige dag
    if (eventEnd.getHours() === 0 && eventEnd.getMinutes() === 0 && eventEnd.getTime() > eventStart.getTime()) {
      eventEnd.setMilliseconds(-1);
    }

    const eventEndStr = this.toLocalString(eventEnd);
    const weekDaysStr = this.weekPlan.map(d => this.toLocalString(d.date));

    if (eventEndStr > weekDaysStr[weekDaysStr.length - 1]) {
      return this.weekPlan.length - 1;
    }

    const index = weekDaysStr.indexOf(eventEndStr);
    return index >= 0 ? index : this.weekPlan.length - 1;
  }

  getMultiDayWidthPercent(event: any): number {
    const startIndex = this.getVisibleStartDayIndex(event);
    const endIndex = this.getVisibleEndDayIndex(event);
    const span = Math.max(1, endIndex - startIndex + 1);
    return span * 100;
  }

  eventOccursOnDay(event: any, dayDate: Date | string): boolean {
    const dayStart = this.startOfDay(this.toDate(dayDate)).getTime();
    const dayEnd = this.endOfDay(this.toDate(dayDate)).getTime();
    const eventStart = this.toDate(event.startDate).getTime();
    const eventEnd = this.toDate(event.endDate).getTime();

    return eventStart <= dayEnd && eventEnd >= dayStart;
  }

  isSameDay(a: Date | string, b: Date | string): boolean {
    const da = this.toDate(a);
    const db = this.toDate(b);

    return da.getFullYear() === db.getFullYear()
      && da.getMonth() === db.getMonth()
      && da.getDate() === db.getDate();
  }

  startOfDay(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  endOfDay(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(23, 59, 59, 999);
    return copy;
  }

  toDate(value: Date | string): Date {
    return value instanceof Date ? value : new Date(value);
  }

  getEventKey(event: any): string {
    const id = event.eventId || event.id || event.title;
    const start = this.toDate(event.startDate).getTime();

    // Combineer ID met de specifieke starttijd van deze instantie
    return `${id}_${start}`;
  }

  toEventBackground(color?: string): string {
    if (!color) {
      return 'rgba(48, 133, 39, 0.12)';
    }

    return this.hexToRgba(color, 0.14);
  }

  hexToRgba(hex: string, alpha: number): string {
    const safeHex = (hex || '').replace('#', '');

    if (safeHex.length !== 6) {
      return `rgba(48, 133, 39, ${alpha})`;
    }

    const r = parseInt(safeHex.substring(0, 2), 16);
    const g = parseInt(safeHex.substring(2, 4), 16);
    const b = parseInt(safeHex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }




  // Sla de berekende lanes op om herberekening te voorkomen tijdens de render-cycle
  private multiDayLanes = new Map<string, number>();

  calculateMultiDayLanes(): void {
    this.multiDayLanes.clear();
    const events = this.getMultiDayEvents();
    const weekDays = this.weekPlan.map(d => this.startOfDay(this.toDate(d.date)).getTime());

    // Grid om bij te houden welke lane op welke dag bezet is
    // lanes[laneIndex][dayIndex] = boolean
    const lanes: boolean[][] = [];

    events.forEach(event => {
      const startIdx = this.getVisibleStartDayIndex(event);
      const endIdx = this.getVisibleEndDayIndex(event);

      let selectedLane = 0;
      let foundLane = false;

      while (!foundLane) {
        if (!lanes[selectedLane]) lanes[selectedLane] = new Array(7).fill(false);

        // Check of deze lane vrij is voor de gehele duur van het event
        const isFree = lanes[selectedLane].slice(startIdx, endIdx + 1).every(occupied => !occupied);

        if (isFree) {
          // Bezet de lane
          for (let i = startIdx; i <= endIdx; i++) {
            lanes[selectedLane][i] = true;
          }
          this.multiDayLanes.set(this.getEventKey(event), selectedLane);
          foundLane = true;
        } else {
          selectedLane++;
        }
      }
    });
  }

  // Helper om de lane op te halen in de HTML
  getEventLane(event: any): number {
    return this.multiDayLanes.get(this.getEventKey(event)) ?? 0;
  }

  onCellContextMenu(event: MouseEvent, dayDate: Date, hour: number, minutes: number = 0): void {
    event.preventDefault();

    const startDate = new Date(dayDate);
    startDate.setHours(hour, minutes, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(startDate.getMinutes() + 30);

    // Bepaal de exacte topPx voor het 30-minuten vakje
    const slotTop = (hour * this.hourSlotHeight) + (minutes === 30 ? this.hourSlotHeight / 2 : 0);
    const slotHeight = this.hourSlotHeight / 2;

    this.selectedSlot = {
      date: startDate,
      topPx: slotTop,
      heightPx: slotHeight
    };

    this.addEventAt(startDate, endDate);
  }

  onAllDayContextMenu(event: MouseEvent, dayDate: Date): void {
    event.preventDefault();
    this.selectedSlot = null;

    const startDate = new Date(dayDate);
    startDate.setHours(9, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setHours(10, 0, 0, 0);

    this.addEventAt(startDate, endDate);
  }

  addEventAt(startDate: Date, endDate: Date): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "event-modal-component";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.height = "575px";
    dialogConfig.width = "950px";
    dialogConfig.data = {
      calendarId: this.currentCalendar?.id,
      startDate: startDate,
      endDate: endDate,
      isNew: true
    };

    const modalDialog = this.matDialog.open(EventComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(async result => {
      this.selectedSlot = null; // Clear selection highlight

      if (!result) return;

      // Nieuw event toevoegen (POST)
      if (result.event || result.action === 'save') {
        const eventPayload = result.event || result.payload;
        const storedEvent = await firstValueFrom(this.eventService.addEvent(eventPayload));
        if (storedEvent) {
          // Direct lokaal verwerken
          this.upsertEventLocally(storedEvent);
        } else {
          // Fallback als de backend (bijv. bij CalDAV) nog geen DTO retourneert
          this.getCalendarData().subscribe();
        }
      }
    });
  }

  editEvent(event: MouseEvent, eventData: EventViewDTO): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedSlot = null;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "event-modal-component";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.data = eventData;
    dialogConfig.height = "575px";
    dialogConfig.width = "950px";

    const modalDialog = this.matDialog.open(EventComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(async result => {
      if (!result) return;

      // 1. AFHANDELING VERWIJDEREN (DELETE)
      if (result.action === 'delete') {
        const payload = result.payload || result;
        const scope = payload.scope || 'ALL_EVENTS';
        const occurrenceDate = payload.instanceStartDate || '';

        // Direct lokaal uit de UI halen
        this.removeEventLocally(payload.id, scope, occurrenceDate);

        // Verzoek naar backend sturen
        await firstValueFrom(this.eventService.deleteEvent(payload.id, scope, occurrenceDate));

        // 2. AFHANDELING BEWERKEN / OPSLAAN (PUT)
      } else if (result.event || result.action === 'save') {
        const eventPayload = result.event || result.payload;
        const scope = eventPayload.editScope || 'ALL_EVENTS';
        const instanceStartDate = eventPayload.instanceStartDate;

        const storedEvent = await firstValueFrom(this.eventService.updateEvent(eventPayload.id, scope, eventPayload));

        if (storedEvent) {
          // Direct lokaal bijwerken
          this.upsertEventLocally(storedEvent, scope, instanceStartDate);
        } else {
          // Fallback
          this.getCalendarData().subscribe();
        }
      }
    });
  }

  addTask(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.height = "530px";
    dialogConfig.width = "950px";

    const modalDialog = this.matDialog.open(TaskComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data && data.task) {
        const task = data.task;
        task.calendarId = this.currentCalendar.id;
        task.sequence = 0;
        this.saveTask(task, true);
      }
    });
  }
}